'use strict';

import _ from 'lodash';

import './Form.less';

import classnames from 'classnames';
import React from 'react';
import PropTypes from 'prop-types';
import RoundButton from './RoundButton';

export const SUBMIT_STATE = {
	// Submitting
	PENDING: 'pending',

	// Just had an error
	ERROR: 'error',

	// Not valid
	FILLING: 'filling',
	// Verifiers pass, ready to submit
	READY: 'ready',

	// Just done, show good status
	DONE: 'done'
};

export default class Form extends React.Component {
	constructor(props) {
		super(props);
		const inputs = {};

		this.state = {
			submitState: this.props.initialState,
			errorMessage: this.props.errorMessage,
			inputs
		};

		React.Children.map(this.filterChildrenForInputs(), child => {
			if (!child) {
				return;
			}

			const { name } = child.props;

			if (!name) {
				return;
			}

			if (this.state.inputs[name]) {
				throw new Error(`There is already an input in this form with the name ${name}`);
			}

			inputs[name] = this.createInput(child);
		});

		if (this.props.initialState !== SUBMIT_STATE.ERROR && this.props.errorMessage) {
			throw new Error('Must set initialState to ERROR if setting an errorMessage in props');
		}

		this.errorMessageRef = React.createRef();
		this.handleSubmit = this.handleSubmit.bind(this);
	}

	componentWillReceiveProps(nextProps) {
		if (nextProps.errorMessage != this.state.errorMessage) {
			this.setState({
				errorMessage: nextProps.errorMessage
			});
		}
	}

	createInput(child) {
		const { value, emptyValue = '' } = child.props;
		return {
			value: value || value === 0 ? value : emptyValue,
			validity: false,
			hasChanged: false,
			hasBeenValidated: false
		};
	}

	handleSubmit(event) {
		_.invoke(event, 'preventDefault');

		this.setState({
			errorMessage: null,
			submitState: SUBMIT_STATE.PENDING
		});

		(async () => {
			try {
				// TODO: Should be able to pass only changed values too
				await this.props.handleSubmit(_.mapValues(this.state.inputs, 'value'));

				this.setState({
					submitState: SUBMIT_STATE.DONE
				});

				await Promise.delay(2000);

				this.setState(prevState => {
					if (prevState.submitState === SUBMIT_STATE.DONE) {
						return {
							submitState: this.props.initialState
						};
					} else {
						return prevState;
					}
				});
			} catch (err) {
				this.setState({
					errorMessage: err.message,
					submitState: SUBMIT_STATE.ERROR
				});
			}
		})();
	}

	fillValues(values) {
		this.setState(prevState => {
			const update = {
				inputs: {}
			};

			for (let name in values) {
				_.set(update, ['inputs', name, 'value'], values[name]);
			}

			Object.keys(update.inputs).forEach(item => {
				update.inputs[item].hasChanged = true;
				update.inputs[item].validity = true;
			});

			update.submitState = SUBMIT_STATE.READY;

			return _.merge({}, prevState, update);
		});
	}

	filterChildrenForInputs() {
		return _.compact(this.props.children);
	}

	render() {
		const buttonProps = _.omitBy(
			{
				disabled: !_.includes([SUBMIT_STATE.READY, SUBMIT_STATE.ERROR], this.state.submitState),
				pending: this.state.submitState === SUBMIT_STATE.PENDING,
				error: this.state.submitState === SUBMIT_STATE.ERROR,
				buttonClass: this.state.submitState === SUBMIT_STATE.DONE ? 'done' : null,
				text: this.props.buttonText,
				size: this.props.buttonSize,
				handleClick: this.handleSubmit
			},
			_.isNil
		);

		const classes = classnames('Form', this.props.className);

		let button;

		if (this.props.customSubmitButton) {
			if (this.state.submitState !== SUBMIT_STATE.DONE) {
				delete buttonProps.text;
			}
			if (typeof this.props.customSubmitButton === 'function') {
				// Function components
				button = React.createElement(this.props.customSubmitButton, buttonProps);
			} else {
				button = React.cloneElement(this.props.customSubmitButton, buttonProps);
			}
		} else {
			button = <RoundButton {...buttonProps} />;
		}

		const matchedChildren = {};
		const allFieldsRequired = _.every(
			React.Children.map(this.filterChildrenForInputs(), child => !child.props.optional)
		);

		const children = React.Children.map(this.props.children, child => {
			if (!child) {
				return;
			}

			const { name, optional } = child.props;

			if (!name) {
				return child;
			}

			let input = this.state.inputs[name];

			if (!input) {
				input = this.createInput(child);

				this.setState(prevState => {
					const previousInputs = prevState.inputs;

					previousInputs[name] = input;

					return {
						inputs: previousInputs
					};
				});
			}

			matchedChildren[name] = true;
			// Apply valid styling if value has changed and still passes validation
			const styleAsValid = input.validity && (!!input.value || input.hasChanged);

			// If all fields are required, don't have asterisks after each label, otherwise do
			const asteriskRequired = !allFieldsRequired && !optional;
			return React.cloneElement(child, {
				valid: styleAsValid,
				error: input.error,
				value: input.value,
				hasAsterisk: asteriskRequired,
				disabled: child.props.disabled,
				onChange: value => {
					this.setState(prevState => {
						const input = prevState.inputs[name];

						if (input === value) {
							return;
						}

						return {
							inputs: _.merge(prevState.inputs, {
								[name]: {
									value,
									hasChanged: true
								}
							})
						};
					});

					if (child.props.onChange) {
						child.props.onChange(value);
					}
				},
				onValidate: (isValid, error) => {
					this.setState(prevState => {
						const newState = {
							inputs: _.merge(prevState.inputs, {
								[name]: {
									validity: isValid,
									error: isValid ? null : error,
									hasBeenValidated: true
								}
							})
						};

						const allValid = _.every(
							_.mapValues(newState.inputs, 'validity'),
							validity => validity || _.isNil(validity)
						);

						if (allValid && prevState.submitState !== SUBMIT_STATE.READY) {
							newState.submitState = SUBMIT_STATE.READY;
						} else if (!allValid && prevState.submitState === SUBMIT_STATE.READY) {
							newState.submitState = SUBMIT_STATE.FILLING;
						}

						return newState;
					});
				}
			});
		});

		let removedChildren = [];

		for (let name in this.state.inputs) {
			if (!matchedChildren[name]) {
				removedChildren.push(name);
			}
		}

		if (removedChildren.length) {
			this.setState(prevState => {
				return {
					inputs: _.omit(prevState.inputs, removedChildren)
				};
			});
		}

		return (
			<form className={classes} onSubmit={this.handleSubmit}>
				{children}
				{button}
				<p
					ref={this.errorMessageRef}
					className="errorMessage"
					dangerouslySetInnerHTML={{
						__html: this.state.errorMessage ? this.state.errorMessage : ''
					}}
				/>
			</form>
		);
	}
}

Form.defaultProps = {
	initialState: SUBMIT_STATE.FILLING,
	buttonText: 'Submit'
};

Form.propTypes = {
	buttonText: PropTypes.string,
	buttonSize: PropTypes.string,
	handleSubmit: PropTypes.func,
	initialState: PropTypes.oneOf([SUBMIT_STATE.READY, SUBMIT_STATE.FILLING]),
	errorMessage: PropTypes.string,
	className: PropTypes.string,
	children: PropTypes.node,
	customSubmitButton: PropTypes.oneOfType([PropTypes.node, PropTypes.func])
};
